import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import type { AriaAttributes } from 'react';
import { useState } from 'react';

import CalendarIcon from '@/assets/svg/bx-calendar-alt.svg?react';
import { Button, type ButtonSize } from '@/components/buttons';
import Calendar from '@/components/Calendar';
import Popover, { PopoverContent, PopoverTrigger } from '@/components/Popover';
import { USDateViewFormat } from '@/constants/formats';
import { cn } from '@/lib/utils';

export type DatePickerProps = {
  id?: string;
  'aria-invalid'?: AriaAttributes['aria-invalid'];
  className?: string;
  disabled?: boolean;
  align?: 'start' | 'center' | 'end';
  size?: ButtonSize;
  value?: Date | undefined;
  onValueChange?: (value: Date | undefined) => void;
};

const DatePicker = ({
  id,
  'aria-invalid': ariaInvalid,
  className,
  disabled,
  align = 'start',
  size = 'large',
  value,
  onValueChange,
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);

  const handleDateChange = (date: Date | undefined) => {
    onValueChange?.(date);
    setOpen(false);
  };

  const val = value ? dayjs(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            aria-invalid={ariaInvalid}
            className={cn(
              'justify-start',
              'border border-gray-600 bg-white font-normal',
              'hover:border-gray-900 hover:bg-white',
              'active:border-green-500 active:bg-white',
              'focus:border-green-500 focus:bg-white focus:ring-1 focus:ring-green-500/50 focus:outline-none focus-visible:bg-white',
              'aria-expanded:border-green-500 aria-expanded:ring-1 aria-expanded:ring-green-500/50 aria-expanded:ring-offset-0',
              'aria-invalid:border-red-400 aria-invalid:hover:border-red-500',
              'aria-invalid:focus:border-red-500 aria-invalid:focus:ring-red-500/50',
              'aria-invalid:aria-expanded:border-red-500 aria-invalid:aria-expanded:ring-red-500/50',
              'disabled:cursor-not-allowed disabled:border-gray-400 disabled:bg-gray-100 disabled:text-gray-400',
              className,
            )}
            variant="ghost"
            shape="square"
            size={size}
            disabled={disabled}
          >
            <CalendarIcon data-icon="inline-start" />
            <span>{val ? val.format(USDateViewFormat) : <Trans>Pick a date</Trans>}</span>
          </Button>
        }
      />
      <PopoverContent className="w-fit p-0 shadow-2xl" align={align}>
        <Calendar
          mode="single"
          captionLayout="dropdown-years"
          defaultMonth={val?.toDate() ?? dayjs().toDate()}
          disabled={{ after: dayjs().endOf('d').toDate() }}
          startMonth={dayjs(new Date(2023, 0, 1)).toDate()}
          endMonth={dayjs().toDate()}
          required
          showOutsideDays={false}
          selected={val?.toDate()}
          onSelect={handleDateChange}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
